
import React from 'react'

import type { Product } from 'types'

import {  useTheme, Box, Skeleton, Stack, Typography } from '@mui/material'

import { Image } from '@repo/ui'
import { Preview } from 'components'
// VRG-777 — `products.images` holds stills AND video in one ordered list, so `images[0]`
// is no longer necessarily a still. `getProductThumbnail` returns the first non-video
// entry; shared in @repo/types so this branch is not re-written at each of the ~20 sites
// that read the column (§36).
import { getProductThumbnail } from '@repo/types'



interface ProductPreviewProps {
  product: Product | null | undefined,
  onChange?: any,
  onRemove?: any,
  disabled?: boolean
  size?: 'small' | 'medium' | 'large'
  description?: string | boolean,
  rightComponents?: React.ReactNode
  sx?: object
}

export const ProductPreview = (props: ProductPreviewProps) => {
  const { product, onRemove = false, size = 'large', onChange = false, disabled = false, description, rightComponents, sx } = props
  const theme = useTheme()

  if (!product) return null

  // VRG-777 — the first NON-VIDEO entry, which is not necessarily `images[0]` any more.
  // `hasImages` now means "there is a still to show", which is the question this preview
  // actually asks: a product whose only media is a clip has no frame for a 64px tile, so
  // it takes the same placeholder branch as a product with no media at all.
  const imageSrc = getProductThumbnail(product.images) ?? ''
  const hasImages = Boolean(imageSrc)

  return (
    <Preview sx={{...sx}} size={size} item={product} onRemove={onRemove} onChange={onChange} disabled={disabled} rightComponents={rightComponents}>
      <Stack direction="row" alignItems="center" spacing={size == 'small' ? 2 : 4} >
        <Box sx={theme => ({ border: `1px solid ${theme.palette.primary[200]}`, borderRadius: 2})}>
          {hasImages ? (
            <Image
              src={imageSrc}
              alt={product?.title || ''}
              fit="cover"
              sx={theme => ({ 
                borderRadius: 2,
                width: size == 'large' ? 64 : size == 'medium' ? 48 : 28, 
                height: size == 'large' ? 64 : size == 'medium' ? 48 : 28 
              })}
            />
          ) : (
            <Box
              sx={theme => ({ 
                borderRadius: 2,
                width: size == 'large' ? 64 : size == 'medium' ? 48 : 28, 
                height: size == 'large' ? 64 : size == 'medium' ? 48 : 28,
                backgroundColor: theme.palette.primary[100],
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              })}
            >
              <Typography variant="caption" color="text.secondary">
                No Image
              </Typography>
            </Box>
          )}
        </Box>
        <Stack spacing={2} sx={{ minWidth: 0, maxWidth: size == 'small' ? 100 : '100%' }}>
          <Typography variant="body2" noWrap>
            {product?.title}
          </Typography>
          {size != 'small' && description != false && (
            <Typography variant="body3" color="text.secondary" noWrap>
              {description ? description : product?.sku}
            </Typography>
          )}
        </Stack>
      </Stack>
    </Preview>
  )
}

export const ProductPreviewLoader = () => {
  return (
    <Box p={3} sx={theme => ({ border: `1px solid ${theme.palette.primary[300]}` })}>
      <Stack spacing={4} direction="row" alignItems="center">
        <Box>
          <Skeleton variant="rectangular" width={64} height={64} />
        </Box>
        <Stack spacing={2} width={'100%'}>
          <Skeleton variant="text" sx={{ fontSize: '1rem' }} width={'100%'} />
          <Skeleton variant="text" sx={{ fontSize: '1rem' }} width={'100%'} />
        </Stack>
      </Stack>
    </Box>
  )
}


export const ProductPreviewAutoComplete = (props: ProductPreviewProps) => {
  const { product } = props

  if (!product) return null

  // VRG-777 — the first NON-VIDEO entry, which is not necessarily `images[0]` any more.
  // `hasImages` now means "there is a still to show", which is the question this preview
  // actually asks: a product whose only media is a clip has no frame for a 64px tile, so
  // it takes the same placeholder branch as a product with no media at all.
  const imageSrc = getProductThumbnail(product.images) ?? ''
  const hasImages = Boolean(imageSrc)

  return (
    <Box p={3} sx={theme => ({borderRadius: 2 })}>
      <Stack spacing={4} direction="row" alignItems="center">
          {hasImages ? (
            <Image
              src={imageSrc}
              alt={product?.title || ''}
              fit="cover"
              sx={theme => ({ width: 48, height: 48, borderRadius: 2, border: `1px solid ${theme.palette.primary[300]}` })}
            />
          ) : (
            <Box
              sx={theme => ({ 
                width: 48, 
                height: 48, 
                borderRadius: 2, 
                border: `1px solid ${theme.palette.primary[300]}`,
                backgroundColor: theme.palette.primary[100],
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              })}
            >
              <Typography variant="caption" color="text.secondary">
                No Image
              </Typography>
            </Box>
          )}
        <Stack spacing={2}>
          <Typography variant="body2" noWrap>
            {product?.title}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {product?.sku}
          </Typography>
        </Stack>
      </Stack>
    </Box>
  )
}
